var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RedeemIcon from '@mui/icons-material/Redeem';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import addPayout from 'root/src/client/logic/payoutMethod/thunks/addPayout';
import { useUser, useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Input } from 'root/src/client/v2/common/components/ui/input';
import { Field } from 'root/src/client/v2/common/components/ui/field';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { MIN_POINTS_PAYOUT_AMOUNT } from 'root/src/shared/constants/pointsCashOut';
import { pointsMinCcv } from 'root/src/shared/constants/points';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { SubmitPointsDialog } from './SubmitPointsDialog';
import { TransactionHistoryList } from './TransactionHistoryList';
import { formatPointsCashValue } from './usePointsTransactions';
const payoutSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});
export const PaymentsPanel = () => {
    var _a, _b, _c;
    const dispatch = useAppDispatch();
    const utils = trpc.useUtils();
    const payoutQuery = trpc.account.getPayoutMethod.useQuery();
    const { creatorPoints, averageCcv } = useUserCreatorData();
    const { userData } = useUser();
    const [editing, setEditing] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const { isLoading } = payoutQuery;
    const currentEmail = (_a = payoutQuery.data) === null || _a === void 0 ? void 0 : _a.email;
    const cashValueLabel = formatPointsCashValue(creatorPoints);
    // Mirror the legacy payoutMethodPointsSelector: only show cash-out + history to creators who can earn
    // points (CCV over the threshold, or they have already earned points).
    const canCashOutPoints = (averageCcv !== null && averageCcv !== void 0 ? averageCcv : 0) >= pointsMinCcv || Boolean((_b = userData === null || userData === void 0 ? void 0 : userData.creatorData) === null || _b === void 0 ? void 0 : _b.isUserEarnedPoints);
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(payoutSchema),
        defaultValues: { email: currentEmail !== null && currentEmail !== void 0 ? currentEmail : '' },
    });
    const startEditing = () => {
        reset({ email: currentEmail !== null && currentEmail !== void 0 ? currentEmail : '' });
        setEditing(true);
    };
    const copyEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!currentEmail) {
            return;
        }
        try {
            yield navigator.clipboard.writeText(currentEmail);
            toast.success('Copied to clipboard');
        }
        catch (e) {
            toast.error('Could not copy to clipboard');
        }
    });
    const onSubmit = handleSubmit(({ email }) => __awaiter(void 0, void 0, void 0, function* () {
        setFailed(false);
        try {
            const result = yield dispatch(addPayout({ email }));
            if (result === null || result === void 0 ? void 0 : result.error) {
                throw result.error;
            }
            yield utils.account.getPayoutMethod.invalidate();
            setSaved(true);
            toast.success('Payout method saved', { description: email });
            setTimeout(() => {
                setSaved(false);
                setEditing(false);
            }, 1100);
        }
        catch (e) {
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
            toast.error('Could not save payout method', { description: 'Please try again.' });
        }
    }), (formErrors) => {
        var _a, _b;
        toast.error('Invalid email', { description: (_b = (_a = formErrors.email) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'Please check the form.' });
    });
    return (<div className="space-y-6">
			<div>
				<h3 className="mb-1 text-sm font-semibold">Payment Methods</h3>
				<p className="mb-4 text-xs text-muted-foreground">Manage how you receive payouts and process transactions.</p>

				<AnimatePresence mode="wait" initial={false}>
					{editing ? (<motion.div key="edit" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
							<form onSubmit={onSubmit} className="space-y-3 rounded-xl border border-solid border-border/50 bg-secondary/30 p-4">
								<Field label="PayPal email" htmlFor="payout-email" error={(_c = errors.email) === null || _c === void 0 ? void 0 : _c.message}>
									<Input id="payout-email" type="email" placeholder="you@example.com" error={!!errors.email} {...register('email')}/>
								</Field>
								<div className="flex justify-end gap-2">
									<Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8 rounded-lg text-[11px]">Cancel</Button>
									<Button type="submit" size="sm" loading={isSubmitting} success={saved} error={failed} className="h-8 min-w-[84px] rounded-lg text-[11px]">Save</Button>
								</div>
							</form>
						</motion.div>) : (<motion.div key="view" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
							<div className="flex items-center justify-between gap-3 rounded-xl border border-solid border-border/50 bg-secondary/30 p-4">
								<div className="flex min-w-0 items-center gap-3">
									<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-solid border-accent/20 bg-accent/10">
										<CreditCardIcon sx={{ fontSize: 20 }} className="text-accent"/>
									</div>
									<div className="min-w-0 space-y-1">
										{isLoading && (<>
												<Skeleton className="h-3 w-32"/>
												<Skeleton className="h-2.5 w-44 max-w-full"/>
											</>)}
										{!isLoading && currentEmail && (<>
												<TooltipProvider delayDuration={200}>
													<div className="flex min-w-0 items-center gap-1.5">
														<Tooltip>
															<TooltipTrigger asChild>
																<p className="cursor-default truncate text-xs font-medium">{currentEmail}</p>
															</TooltipTrigger>
															<TooltipContent className="max-w-[260px] break-all">{currentEmail}</TooltipContent>
														</Tooltip>
														<Tooltip>
															<TooltipTrigger asChild>
																<button type="button" onClick={copyEmail} aria-label="Copy email" className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:text-foreground [&_svg]:size-3.5">
																	<ContentCopyIcon />
																</button>
															</TooltipTrigger>
															<TooltipContent>Copy email</TooltipContent>
														</Tooltip>
													</div>
												</TooltipProvider>
												<p className="truncate text-[10px] text-muted-foreground">PayPal payout email</p>
											</>)}
										{!isLoading && !currentEmail && (<>
												<p className="truncate text-xs font-medium">No payment method</p>
												<p className="truncate text-[10px] text-muted-foreground">Add a payment method to receive payouts</p>
											</>)}
									</div>
								</div>
								{isLoading ? (<Skeleton className="h-7 w-14 flex-shrink-0 rounded-lg"/>) : (<Button size="sm" variant="outline" onClick={startEditing} className="h-7 flex-shrink-0 rounded-lg text-[11px]">
										{currentEmail ? 'Edit' : 'Add'}
									</Button>)}
							</div>
						</motion.div>)}
				</AnimatePresence>
			</div>

			{canCashOutPoints && (<>
					<div>
						<h3 className="mb-1 text-sm font-semibold">Points</h3>
						<p className="mb-4 text-xs text-muted-foreground">
							Accept a dare and stream games in drops to earn points. The more viewership you get, the more points are rewarded!
						</p>
						<div className="rounded-xl border border-solid border-border/50 bg-secondary/30 p-4">
							<div className="flex items-center justify-between gap-3">
								<div className="flex min-w-0 items-center gap-3">
									<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-solid border-accent/20 bg-accent/10">
										<RedeemIcon sx={{ fontSize: 20 }} className="text-accent"/>
									</div>
									<div className="min-w-0 space-y-1">
										<p className="truncate text-xs font-medium">{formatNumberWithCommas(creatorPoints)} points</p>
										<p className="truncate text-[10px] text-muted-foreground">{cashValueLabel} cash value</p>
									</div>
								</div>
								<Button size="sm" variant="brand" onClick={() => setSubmitOpen(true)} className="h-7 flex-shrink-0 rounded-lg text-[11px]">
									Submit points
								</Button>
							</div>
							<p className="mt-3 text-[11px] text-neon-pink">
								You must have at least {MIN_POINTS_PAYOUT_AMOUNT / 1000}k minimum points in order to submit.
							</p>
						</div>
						<p className="mt-2 text-[11px] text-muted-foreground">Expect PayPal delivery 8 days from submission.</p>
						<SubmitPointsDialog open={submitOpen} onOpenChange={setSubmitOpen} creatorPoints={creatorPoints}/>
					</div>

					<div>
						<h3 className="mb-1 text-sm font-semibold">Transaction History</h3>
						<p className="mb-4 text-xs text-muted-foreground">View your past transactions and payouts.</p>
						<TransactionHistoryList />
					</div>
				</>)}
		</div>);
};
